import { Base64 } from 'js-base64';
import moment from 'moment';

import type { IAccount, IUser } from '@share/common-types';
import { LOGGED_USER_LABEL, WALLET_LOGGED_USER_LABEL } from '@share/constants';

import { IsClientCashEnabled } from './accounts';

interface ILoggedUserStoragePayload {
  user?: IUser;
  date?: string;
}

function readLoggedUserStorage(): ILoggedUserStoragePayload | null {
  const loggedUser = localStorage.getItem(LOGGED_USER_LABEL);
  if (!loggedUser) {
    return null;
  }

  return JSON.parse(Base64.decode(loggedUser)) as ILoggedUserStoragePayload;
}

function readWalletUserStorage(): IUser | null {
  const loggedUser = localStorage.getItem(WALLET_LOGGED_USER_LABEL);
  if (!loggedUser) {
    return null;
  }

  const userDataStorage = JSON.parse(Base64.decode(loggedUser)) as { user?: IUser };
  return userDataStorage?.user ?? null;
}

function hasValidLoggedUserSession(account: IAccount): boolean {
  const userDataStorage = readLoggedUserStorage();
  const userStorage = userDataStorage?.user;
  const date = userDataStorage?.date;

  if (!userStorage || !date || userStorage.active === 0 || userStorage.accountId !== account.id) {
    return false;
  }

  const dateStorageMoment = moment(date, 'yyyy-MM-DDTHH:mm:ss').add(3, 'd');
  return dateStorageMoment.isAfter(moment());
}

function hasValidWalletUserSession(account: IAccount): boolean {
  const isClientCashEnabled = IsClientCashEnabled(account);

  if (!isClientCashEnabled && !account.enableWalletReward) {
    return false;
  }

  const userStorageWallet = readWalletUserStorage();

  if (!userStorageWallet || userStorageWallet.active === 0) {
    return false;
  }

  return userStorageWallet.accountId === account.id;
}

/** True when browser storage holds a valid member session for the current account. */
export function hasPersistedMemberSession(account: IAccount | null | undefined): boolean {
  if (!account?.id) {
    return false;
  }

  try {
    if (hasValidLoggedUserSession(account)) {
      return true;
    }
  } catch {
    return false;
  }

  try {
    return hasValidWalletUserSession(account);
  } catch {
    return false;
  }
}
